import axios, { type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import type { ConnectionCountingMode } from '../utils/connectionCountingModes';
import type { OptimizationObjectiveFunction } from '../utils/optimizationObjectives';
import type {
  NearMissComparisonRow,
  NearMissSection,
} from '../components/AGGrid/types/flightPairTypes';

// Determine API URL based on environment
const getApiBaseUrl = (): string => {
  const environment = import.meta.env.MODE;

  const withApiPath = (baseUrl: string): string => {
    const normalized = baseUrl.replace(/\/+$/, '');
    return normalized.endsWith('/api') ? normalized : `${normalized}/api`;
  };

  if (environment === 'production') {
    return withApiPath(
      import.meta.env.VITE_API_URL_PROD || 'https://connection-calculator-bp.fly.dev'
    );
  }

  return withApiPath(import.meta.env.VITE_API_URL_DEV || 'http://127.0.0.1:8000');
};

const API_BASE_URL = getApiBaseUrl();
const EXPLORE_GET_MAX_QUERY_LENGTH = 1800;

// --- Auth token storage ---
// Production: in-memory only (never in localStorage/sessionStorage).
// Dev: persisted locally so Vite full reloads, HMR misses, and tab replacement
// during localhost work do not force a fresh login every time code changes.
const DEV_TOKEN_KEY = 'lm_dev_access_token';
const isDev = import.meta.env.DEV;
const DEV_AUTH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const getDevCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split('=').slice(1).join('='));
};

const setDevCookie = (name: string, value: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${DEV_AUTH_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};

const clearDevCookie = (name: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
};

const getDevStorage = (kind: 'local' | 'session'): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const readDevAccessToken = (): string | null => {
  const local = getDevStorage('local');
  const session = getDevStorage('session');
  const token =
    local?.getItem(DEV_TOKEN_KEY) ||
    session?.getItem(DEV_TOKEN_KEY) ||
    getDevCookie(DEV_TOKEN_KEY);

  if (token && local && local.getItem(DEV_TOKEN_KEY) !== token) {
    local.setItem(DEV_TOKEN_KEY, token);
  }
  if (token && session && session.getItem(DEV_TOKEN_KEY) !== token) {
    session.setItem(DEV_TOKEN_KEY, token);
  }
  if (token && getDevCookie(DEV_TOKEN_KEY) !== token) {
    setDevCookie(DEV_TOKEN_KEY, token);
  }

  return token || null;
};

const writeDevAccessToken = (token: string | null): void => {
  const local = getDevStorage('local');
  const session = getDevStorage('session');

  if (token) {
    local?.setItem(DEV_TOKEN_KEY, token);
    session?.setItem(DEV_TOKEN_KEY, token);
    setDevCookie(DEV_TOKEN_KEY, token);
    return;
  }

  local?.removeItem(DEV_TOKEN_KEY);
  session?.removeItem(DEV_TOKEN_KEY);
  clearDevCookie(DEV_TOKEN_KEY);
};

let _accessToken: string | null = isDev ? readDevAccessToken() : null;
let _onLogout: (() => void) | null = null;
const REFRESH_TOKEN_STORAGE_KEY = 'fca.refreshToken';

export function setAccessToken(token: string | null): void {
  _accessToken = token;
  if (isDev) {
    writeDevAccessToken(token);
  }
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function setLogoutCallback(fn: () => void): void {
  _onLogout = fn;
}

export function setRefreshToken(token: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (token) {
      sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
  } catch {
    // Keep cookie-backed refresh working when browser storage is unavailable.
  }
}

export function getRefreshToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearAuthTokens(): void {
  setAccessToken(null);
  setRefreshToken(null);
}

// --- CSRF helpers ---
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));
  if (!cookie) return null;
  return decodeURIComponent(cookie.split('=').slice(1).join('='));
}

export function getCsrfToken(): string | null {
  return getCookie('csrftoken');
}

export async function ensureCsrfCookie(): Promise<string | null> {
  const existing = getCsrfToken();
  if (existing) return existing;
  await axios.get(`${API_BASE_URL}/auth/csrf/`, { withCredentials: true });
  return getCsrfToken();
}

export async function getCsrfHeaders(): Promise<Record<string, string>> {
  const token = await ensureCsrfCookie();
  return token ? { 'X-CSRFToken': token } : {};
}

// --- Axios instance ---
// Exported for feature API modules kept outside this file (e.g. routeOptimizerApi).
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// --- Request interceptor: attach Bearer token ---
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (_accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${_accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// --- Response interceptor: refresh on 401, retry, or logout ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

async function refreshAccessTokenForRetry(): Promise<string> {
  const csrfHeaders = await getCsrfHeaders();
  const storedRefreshToken = getRefreshToken();
  const { data } = await axios.post(
    `${API_BASE_URL}/auth/token/refresh/`,
    storedRefreshToken ? { refresh: storedRefreshToken } : {},
    { withCredentials: true, headers: csrfHeaders },
  );
  const newToken = data.access_token || data.access;
  if (!newToken) {
    throw new Error('Refresh response did not include an access token');
  }
  const rotatedRefreshToken = data.refresh || data.refresh_token;
  setAccessToken(newToken);
  if (rotatedRefreshToken) {
    setRefreshToken(rotatedRefreshToken);
  }
  return newToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/token/refresh')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessTokenForRetry();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuthTokens();
      if (_onLogout) _onLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

function buildAuthenticatedJsonHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  const csrfToken = getCsrfToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  return headers;
}

async function postHubBankOptimizerStream(
  data: HubOptimizeRequest,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(
    `${API_BASE_URL}/sample-weeks/optimize_hub_bank_stream/`,
    {
      method: 'POST',
      headers: buildAuthenticatedJsonHeaders(),
      credentials: 'include',
      body: JSON.stringify(data),
      signal,
    },
  );
}

/**
 * POST JSON and hand each NDJSON line of the streamed response to `onEvent`,
 * refreshing the access token once on a 401 (as the axios client does).
 */
export async function postNdjsonStream(
  path: string,
  data: unknown,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const post = () => fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: buildAuthenticatedJsonHeaders(),
    credentials: 'include',
    body: JSON.stringify(data),
    signal,
  });
  let response = await post();
  if (response.status === 401 && !signal?.aborted) {
    try {
      await refreshAccessTokenForRetry();
    } catch (refreshError) {
      clearAuthTokens();
      if (_onLogout) _onLogout();
      throw refreshError;
    }
    response = await post();
  }
  if (!response.ok) {
    const text = await response.text();
    let data: unknown = null;
    try {
      data = JSON.parse(text);
    } catch {
      // A proxy's own error page: no structured body.
    }
    // Shaped like an axios error so callers can tell a server answer from a
    // gateway that dropped the request.
    throw Object.assign(new Error(apiErrorMessage(text, `HTTP ${response.status}`)), {
      response: { status: response.status, data },
    });
  }
  if (!response.body) {
    throw new Error('Stream response did not include a body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop()!;
    for (const line of lines) {
      if (line.trim()) onEvent(JSON.parse(line));
    }
  }
  if (buffer.trim()) onEvent(JSON.parse(buffer));
}

function apiErrorMessage(text: string, fallback: string): string {
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text) as { detail?: unknown; error?: unknown };
    if (typeof parsed.detail === 'string') return parsed.detail;
    if (typeof parsed.error === 'string') return parsed.error;
  } catch {
    // Preserve non-JSON server output as-is.
  }
  return text;
}

// Types for API responses
export interface SampleWeek {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  source_file: string;
  parquet_path: string;
  flight_count: number;
  unique_airports: number;
  unique_carriers: number;
  created_at: string;
  updated_at: string;
  source_file_date?: string | null;
  archived_at?: string | null;
}

export interface CreateSampleWeekRequest {
  start_date: string;
  end_date: string;
  name: string;
}

export interface FlightData {
  carrier: string;
  flightnumber: string;
  departure_airport: string;
  arrival_airport: string;
  departure_time_pub: string;
  arrival_time_pub: string;
  departure_country: string;
  arrival_country: string;
  sub_aircraft_code?: string;
  departure_terminal?: string | null;
  arrival_terminal?: string | null;
  flight_date: string;
  day_of_week: number;
  departure_float_time: number;
  arrival_float_time: number;
}

export interface CompressedFlightData {
  carrier: string;
  flightnumber: string;
  departure_airport: string;
  arrival_airport: string;
  departure_time_pub: string;
  arrival_time_pub: string;
  departure_country: string;
  arrival_country: string;
  sub_aircraft_code?: string;
  departure_terminal?: string | null;
  arrival_terminal?: string | null;
  effective_date: string;
  discontinued_date: string;
  frequency: string;
  elapsed_time?: number | null;
  arrival_day_indicator?: number | null;
  source?: string;
}

/** A flight of the viewed version, classified against a baseline version (#869). */
export interface ChangedFlight {
  carrier: string;
  flightnumber: number | string;
  departure_airport: string;
  arrival_airport: string;
  departure_time_pub: string;
  arrival_time_pub: string;
  status: 'added' | 'modified';
  /**
   * The days carrying that status, as a frequency mask ('...4...'). A flight's
   * key has no day dimension, so this is what separates "the whole flight is
   * new" from "it gained a Thursday".
   */
  days: string;
}

export interface ChangedFlightsResponse {
  baseline_version: number;
  version: number;
  changed: ChangedFlight[];
  added_count: number;
  modified_count: number;
  /** True when the baseline is at or after the viewed version — nothing to show. */
  nothing_to_compare: boolean;
}

export interface ScheduleVersion {
  id: string;
  version_number: number;
  is_latest: boolean;
  is_checkpoint: boolean;
  // Editable bookmark shown in the version list; '' when unset (#742).
  label: string;
  // How the version came to be; drives provenance captions (#742).
  created_via: 'edit' | 'baseline' | 'merge' | 'revert' | 'duplicate';
  // The version a revert/duplicate was derived from, else null (#742).
  source_version_number: number | null;
  created_by: string | null;
  // Provenance (#768): platform-stamped when the version was agent-made.
  created_by_agent?: boolean;
  agent_run?: string | null;
  created_at: string;
  description: string;
  change_summary: {
    added: number;
    modified: number;
    deleted: number;
  };
}

export interface VersionListResponse {
  versions: ScheduleVersion[];
  latest_version_number: number;
}

export interface FlightExplorerResponse {
  results: CompressedFlightData[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  version_number: number | null;
}

export interface FlightPair {
  origin: string;
  destination: string;
}

export interface ConnectionCalculationRequest {
  sample_week_id: string;
  hub_airport: string;
  flight_pairs: FlightPair[];
  min_connection_hours: number;
  max_connection_hours: number;
}

export interface FlightDetails {
  carrier: string;
  flight_number: string;
  departure_airport: string;
  arrival_airport: string;
  departure_time: string;
  arrival_time: string;
  flight_date: string;
}

export interface ConnectionResult {
  origin: string;
  hub: string;
  destination: string;
  inbound_flight: FlightDetails;
  outbound_flight: FlightDetails;
  connection_time_hours: number;
}

export interface CompressedConnectionResult {
  route: string;
  inbound_flight: {
    carrier: string;
    flight_number: string;
    arrival_time: string;
  };
  outbound_flight: {
    carrier: string;
    flight_number: string;
    departure_time: string;
  };
  connection_time_hours: number;
  effective_date: string;
  discontinued_date: string;
  frequency: string;
}

export interface ConnectionCalculationResponse {
  connections: CompressedConnectionResult[];
  count: number;
  raw_count: number;
  execution_time_ms: number;
  hub_airport: string;
  query_parameters: {
    flight_pairs: FlightPair[];
    min_connection_hours: number;
    max_connection_hours: number;
  };
}

export type CountingMode = ConnectionCountingMode;
export type MctMode = 'manual' | 'dataset';

export interface MinimumConnectionTimeSelection {
  mct_mode?: MctMode;
  mct_version?: number | null;
}

// Per-option summary stats for the schedule-comparison summary cards.
export interface OptionSummary {
  market_count: number;
  total_connections: number;
  near_miss_count?: number;
  avg_connection_minutes: number | null;
  passenger_pool?: number;
  revenue_pool?: number;
  /** Live total for the requested custom metric; null when it cannot be scored. */
  custom_metric_score?: number | null;
}

// API functions
export const sampleWeekApi = {
  // Create a new sample week
  create: async (data: CreateSampleWeekRequest): Promise<SampleWeek> => {
    // Explicit generous timeout (5 min) so a dropped/slow create can't leave the
    // UI spinning forever; the caller turns an abort into a recoverable message
    // and re-checks the list, since the server may still be finishing (#736).
    const response = await api.post('/sample-weeks/', data, { timeout: 300000 });
    return response.data;
  },

  // List all sample weeks
  list: async (): Promise<SampleWeek[]> => {
    const response = await api.get('/sample-weeks/');
    return response.data.results || response.data;
  },

  // List archived (soft-deleted, restorable) sample weeks
  listArchived: async (): Promise<SampleWeek[]> => {
    const response = await api.get('/sample-weeks/', { params: { archived: 'true' } });
    return response.data.results || response.data;
  },

  // Restore an archived sample week
  restore: async (id: string): Promise<SampleWeek> => {
    const response = await api.post(`/sample-weeks/${id}/restore/`);
    return response.data;
  },

  // Get a specific sample week
  get: async (id: string): Promise<SampleWeek> => {
    const response = await api.get(`/sample-weeks/${id}/`);
    return response.data;
  },

  // Warm cached metadata for non-stop weekly service counts without blocking UI
  warmNonstopService: async (
    id: string,
    versionNumber?: number | null
  ): Promise<{
    status: 'ready' | 'warming';
    sample_week_id: string;
    version_number: number | null;
  }> => {
    const response = await api.post(`/sample-weeks/${id}/warm_nonstop_service/`, {
      ...(versionNumber != null ? { version_number: versionNumber } : {}),
    });
    return response.data;
  },

  // Explore flight data with filters and optional version
  explore: async (
    id: string,
    filters: Record<string, any> = {},
    page = 1,
    pageSize = 100,
    version: number | 'latest' = 'latest'
  ): Promise<FlightExplorerResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      version: version.toString(),
      ...filters,
    });
    const queryString = params.toString();

    if (queryString.length > EXPLORE_GET_MAX_QUERY_LENGTH) {
      const response = await api.post(`/sample-weeks/${id}/explore/`, {
        filters,
        page,
        page_size: pageSize,
        version,
      });
      return response.data;
    }

    const response = await api.get(`/sample-weeks/${id}/explore/?${queryString}`);
    return response.data;
  },

  // Calculate connections
  calculateConnections: async (
    id: string,
    data: Omit<ConnectionCalculationRequest, 'sample_week_id'>
  ): Promise<ConnectionCalculationResponse> => {
    const response = await api.post(`/sample-weeks/${id}/calculate_connections/`, data);
    return response.data;
  },

  // Delete a sample week
  delete: async (id: string): Promise<void> => {
    await api.delete(`/sample-weeks/${id}/`);
  },

  // Calculate single flight pair connections
  calculateSinglePairConnections: async (
    id: string,
    data: {
      hub_airport: string;
      custom_flights: Array<{
        flight_number: string;
        departure: string;
        arrival: string;
        departure_time: string;
        arrival_time: string;
        dow: string;
      }>;
      min_connection_to: number;
      max_connection_to: number;
      min_connection_from: number;
      max_connection_from: number;
      connection_type: string;
      selected_regions: string[];
      counting_mode?: CountingMode;
      label_set_id?: number | null;
    } & MinimumConnectionTimeSelection
  ): Promise<{
    market_connections: Array<{
      market: string;
      nonstop_weekly?: number | null;
      freq_to: number;
      conx_flt_to: string;
      pct_to: number;
      min_elap_to: string;
      freq_from: number;
      conx_flt_from: string;
      pct_from: number;
      min_elap_from: string;
    }>;
    detailed_connections: Record<string, {
      to: any[];
      from: any[];
    }>;
    count: number;
    execution_time_ms: number;
    hub_airport: string;
  }> => {
    const response = await api.post(`/sample-weeks/${id}/calculate_single_pair_connections/`, data);
    return response.data;
  },

  // Calculate multiple flight pairs connections
  calculateMultiplePairConnections: async (
    id: string,
    data: {
      hub_airport: string;
      custom_flights: Array<{
        id: string;
        flight_number: string;
        departure: string;
        arrival: string;
        departure_time: string;
        arrival_time: string;
        dow: string;
        block_time?: number | null;
        time_mode?: 'dep' | 'arr';
        tag: {
          text: string;
          color: string;
        };
      }>;
      min_connection_to: number;
      max_connection_to: number;
      min_connection_from: number;
      max_connection_from: number;
      near_miss_threshold_minutes?: number;
      include_near_misses?: boolean;
      connection_type: string;
      airline_filter?: string[];
      selected_regions: string[];
      version_number?: number | null;
      aggregate_nondirectional?: boolean;
      counting_mode?: CountingMode;
      circuity_filter?: {
        relative_enabled: boolean;
        relative_limit: number;
        absolute_enabled: boolean;
        absolute_limit: number;
        use_and_logic: boolean;
      };
      label_set_id?: number | null;
      custom_metric_id?: string;
      custom_metric_baseline_flights?: Array<{ id: string; flight_number: string; departure: string; arrival: string; departure_time: string; arrival_time: string; dow: string; block_time?: number | null; time_mode?: 'dep' | 'arr'; tag: { text: string; color: string } }>;
    } & MinimumConnectionTimeSelection
  ): Promise<{
    market_connections: Array<{
      market: string;
      country_name?: string;
      region_name?: string;
      nonstop_weekly?: number | null;
      flight_groups: Record<string, {
        tag: {
          text: string;
          color: string;
        };
        freq_to: number;
        freq_from: number;
      }>;
      total_freq_to: number;
      total_freq_from: number;
      min_pct_to: number | null;
      min_pct_from: number | null;
      min_elap_to: string | null;
      min_elap_from: string | null;
      circuity_percent?: string | null;
      circuity_absolute?: string | null;
    }>;
    detailed_connections: Record<string, {
      to: any[];
      from: any[];
    }>;
    near_misses?: NearMissSection | null;
    count: number;
    execution_time_ms: number;
    hub_airport: string;
    custom_metric_score?: number | null;
    custom_metric_baseline_score?: number | null;
    /** The requested custom metric is no longer available to the caller (e.g. unshared). */
    custom_metric_unavailable?: boolean;
  }> => {
    const response = await api.post(`/sample-weeks/${id}/calculate_multiple_pair_connections/`, data);
    return response.data;
  },

  // Compare 2-5 schedule options (per-option hub and connection parameters)
  compareScheduleOptions: async (
    id: string,
    data: {
      options: Array<{
        hub_airport: string;
        flights: Array<{
          flight_number: string;
          departure: string;
          arrival: string;
          departure_time: string;
          arrival_time: string;
          dow: string;
        }>;
        min_connection_to: number;
        max_connection_to: number;
        min_connection_from: number;
        max_connection_from: number;
        near_miss_threshold_minutes?: number;
        include_near_misses?: boolean;
        connection_type: string;
        airline_filter?: string[];
        selected_regions: string[];
        circuity_filter?: {
          relative_enabled: boolean;
          relative_limit: number;
          absolute_enabled: boolean;
          absolute_limit: number;
          use_and_logic: boolean;
        };
        sample_week_id?: string;
        version_number?: number | null;
      } & MinimumConnectionTimeSelection>;
      counting_mode?: CountingMode;
      label_set_id?: number | null;
      custom_metric_id?: string;
      custom_metric_ids?: string[];
    }
  ): Promise<{
    market_comparisons: Array<{
      market: string;
      country_name?: string;
      region_name?: string;
      nonstop_weekly?: number | null;
      [key: string]: any;
    }>;
    option_count: number;
    option_summaries: OptionSummary[];
    near_misses_by_option: Record<string, NearMissSection | null>;
    near_miss_comparisons: NearMissComparisonRow[];
    count: number;
    execution_time_ms: number;
    /** The requested custom metric is no longer available to the caller (e.g. unshared). */
    custom_metric_unavailable?: boolean;
  }> => {
    const response = await api.post(`/sample-weeks/${id}/calculate_schedule_comparison/`, data);
    return response.data;
  },

  // Batch update segments (add, edit, delete)
  batchUpdateSegments: async (
    id: string,
    data: {
      additions?: CompressedFlightData[];
      updates?: Array<{
        match_criteria: {
          carrier?: string;
          flightnumber?: number;
          departure_airport?: string;
          arrival_airport?: string;
          frequency?: string;
        };
        changes: Partial<CompressedFlightData>;
      }>;
      deletions?: Array<{
        carrier?: string;
        flightnumber?: number;
        departure_airport?: string;
        arrival_airport?: string;
        frequency?: string;
      }>;
      description?: string;
      base_version_number?: number;
    }
  ): Promise<{
    success: boolean;
    added_count: number;
    updated_count: number;
    deleted_count: number;
    execution_time_ms: number;
    schedule?: CompressedFlightData[];
    version?: {
      id: string;
      version_number: number;
      is_latest: boolean;
      created_at: string;
      change_summary: {
        added: number;
        modified: number;
        deleted: number;
      };
    };
  }> => {
    const response = await api.post(`/sample-weeks/${id}/batch_update_segments/`, data);
    return response.data;
  },

  // Flights of `version` added or modified relative to `baselineVersion` (#869)
  changedFlights: async (
    id: string,
    baselineVersion: number,
    version: number | 'latest' = 'latest'
  ): Promise<ChangedFlightsResponse> => {
    const params = new URLSearchParams({
      baseline: String(baselineVersion),
      version: String(version),
    });
    const response = await api.get(`/sample-weeks/${id}/changed-flights/?${params.toString()}`);
    return response.data;
  },

  // List all versions for a sample week
  listVersions: async (id: string): Promise<VersionListResponse> => {
    const response = await api.get(`/sample-weeks/${id}/versions/`);
    return response.data;
  },

  // Get flights for a specific version (use 'latest' for most recent)
  getVersionFlights: async (
    id: string,
    versionNumber: number | 'latest',
    page = 1,
    pageSize = 100000
  ): Promise<FlightExplorerResponse & { version: ScheduleVersion }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    const response = await api.get(
      `/sample-weeks/${id}/versions/${versionNumber}/flights/?${params}`
    );
    return {
      results: response.data.flights,
      count: response.data.count,
      page: response.data.page,
      page_size: response.data.page_size,
      total_pages: response.data.total_pages,
      version_number: response.data.version?.version_number ?? null,
      version: response.data.version,
    };
  },

  // Compare two versions
  compareVersions: async (
    id: string,
    fromVersion: number | 'latest',
    toVersion: number | 'latest'
  ): Promise<{
    from_version: number;
    to_version: number;
    changes: {
      added: Array<{ flight_id: string; data: any }>;
      modified: Array<{ flight_id: string; changes: Record<string, { from: any; to: any }> }>;
      deleted: Array<{ flight_id: string; data: any }>;
    };
    summary: {
      added: number;
      modified: number;
      deleted: number;
      total_changes: number;
    };
  }> => {
    const params = new URLSearchParams({
      from: fromVersion.toString(),
      to: toVersion.toString(),
    });
    const response = await api.get(`/sample-weeks/${id}/versions/compare?${params}`);
    return response.data;
  },

  // Revert to an older version: creates a new latest version whose state equals
  // the target version, leaving the intervening history untouched (#742).
  // `baseVersionNumber` is the latest the client last saw — the server 409s if
  // a newer version has since landed (same conflict shape as batchUpdateSegments).
  revertToVersion: async (
    id: string,
    versionNumber: number,
    baseVersionNumber: number
  ): Promise<{ version: ScheduleVersion; latest_version_number: number }> => {
    const response = await api.post(
      `/sample-weeks/${id}/versions/${versionNumber}/revert/`,
      { base_version_number: baseVersionNumber }
    );
    return response.data;
  },

  // Set or clear a version's bookmark label. An empty string clears it (#742).
  updateVersionLabel: async (
    id: string,
    versionNumber: number,
    label: string
  ): Promise<ScheduleVersion> => {
    const response = await api.patch(
      `/sample-weeks/${id}/versions/${versionNumber}/`,
      { label }
    );
    return response.data;
  },

  // Copy a version's schedule into a brand-new sample week (#742).
  duplicateVersionToSampleWeek: async (
    id: string,
    versionNumber: number,
    name: string
  ): Promise<SampleWeek> => {
    const response = await api.post(
      `/sample-weeks/${id}/versions/${versionNumber}/duplicate/`,
      { name }
    );
    return response.data;
  },

  calculatePartnershipAnalysis: async (data: {
    hub_1: PartnershipHubParams;
    hub_2: PartnershipHubParams;
    variants: Array<{ name: string; hub_2?: PartnershipHubParams; trunk_flights: Array<{
      id?: string;
      flight_number: string;
      departure: string;
      arrival: string;
      departure_time: string;
      arrival_time: string;
      dow: string;
      tag?: { text: string; color: string };
      block_time?: number | null;
      time_mode?: 'dep' | 'arr';
    }> }>;
    counting_mode?: CountingMode;
    label_set_id?: number | null;
    custom_metric_id?: string;
  }): Promise<PartnershipAnalysisResult> => {
    const response = await api.post('/sample-weeks/calculate_partnership_analysis/', data);
    return response.data;
  },

  optimizeTrunkSchedule: async (data: OptimizeTrunkPayload): Promise<OptimizeTrunkResult> => {
    const response = await api.post('/sample-weeks/optimize_trunk_schedule/', data);
    return response.data;
  },

  inferRotations: async (data: RotationInferenceRequest): Promise<RotationInferenceResponse> => {
    const response = await api.post('/sample-weeks/infer_rotations/', data);
    return response.data;
  },

  scoreHubSchedule: async (data: HubScoreRequest): Promise<HubScoreResponse> => {
    const response = await api.post('/sample-weeks/score_hub_schedule/', data);
    return response.data;
  },

  optimizeHubBank: async (data: HubOptimizeRequest): Promise<HubOptimizeResponse> => {
    const response = await api.post('/sample-weeks/optimize_hub_bank/', data);
    return response.data;
  },

  optimizeHubBankStream: async (
    data: HubOptimizeRequest,
    onProgress: (p: SolverProgress) => void,
    signal?: AbortSignal,
  ): Promise<HubOptimizeResponse> => {
    try {
      let response = await postHubBankOptimizerStream(data, signal);
      if (response.status === 401 && !signal?.aborted) {
        try {
          await refreshAccessTokenForRetry();
          response = await postHubBankOptimizerStream(data, signal);
        } catch (refreshError) {
          clearAuthTokens();
          if (_onLogout) _onLogout();
          throw refreshError;
        }
      }
      if (!response.ok) {
        const text = await response.text();
        throw new Error(apiErrorMessage(text, `HTTP ${response.status}`));
      }
      if (!response.body) {
        throw new Error('Optimizer stream response did not include a body');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop()!;
        for (const line of lines) {
          if (!line.trim()) continue;
          const msg = JSON.parse(line);
          if (msg.type === 'progress') {
            onProgress(msg as SolverProgress);
          } else if (msg.type === 'result') {
            return msg.data as HubOptimizeResponse;
          } else if (msg.type === 'error') {
            throw new Error(msg.error);
          }
        }
      }
      throw new Error('Stream ended without result');
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        throw new Error('Optimization stopped by user');
      }
      throw err;
    }
  },
};

// Rotation Inference types
export interface RotationInferenceRequest {
  sample_week_id: string;
  version_number?: number;
  hub_airport: string;
  carrier: string;
  day_of_week: number;
  max_ground_time?: number;
  cross_fleet?: boolean;
}

export interface OutstationLink {
  outstation: string;
  subfleet: string;
  subfleet_info: { type: string; aircraft: string; min_ground_time: number };
  outbound_flight: string;
  outbound_dep_hub: string;
  outbound_arr_outstation: string;
  outbound_block_time: number | null;
  return_flight: string;
  return_dep_outstation: string;
  return_arr_hub: string;
  return_block_time: number | null;
  return_day_of_week: number;
  outstation_ground_time_min: number;
  min_ground_time: number;
  slack_min: number;
  total_rotation_time_min: number | null;
  cross_fleet?: boolean;
  return_subfleet?: string | null;
}

export interface HubBalanceEntry {
  arrivals: number;
  departures: number;
  net: number;
}

export interface UnmatchedOutstationFlight {
  flight: string;
  outstation: string;
  subfleet: string;
  dep_hub?: string;
  arr_outstation?: string;
  dep_outstation?: string;
  arr_hub?: string;
  reason: string;
}

export interface RotationInferenceResponse {
  hub: string;
  carrier: string;
  day_of_week: number;
  outstation_links: OutstationLink[];
  hub_balance: Record<string, HubBalanceEntry>;
  broken_turns: UnmatchedOutstationFlight[];
  unmatched_outbound: UnmatchedOutstationFlight[];
  unmatched_return: UnmatchedOutstationFlight[];
  stats: {
    total_hub_departures: number;
    total_outstation_links: number;
    broken_turns: number;
    unmatched_outbound: number;
    unmatched_return: number;
    unique_outstations: number;
    by_subfleet: Record<string, {
      outbound: number;
      matched: number;
      unmatched_out: number;
      unmatched_ret: number;
    }>;
    execution_time_ms: number;
  };
}

// Hub Scorer types
export interface HubScoreRequest {
  sample_week_id: string;
  version_number?: number;
  hub_airport: string;
  carrier: string;
  day_of_week: number;
  min_connection_time: number;
  max_connection_time: number;
  mct_mode?: MctMode;
  mct_version?: number | null;
  circuity_filter?: {
    relative_enabled: boolean;
    relative_limit: number;
    absolute_enabled: boolean;
    absolute_limit: number;
    use_and_logic: boolean;
  };
  capacity_60?: number;
  capacity_15?: number;
}

export interface HubScoreResponse {
  hub: string;
  carrier: string;
  day_of_week: number;
  unique_od_pairs: number;
  total_connections: number;
  hub_carrier_arrivals: number;
  hub_carrier_departures: number;
  total_arrivals: number;
  total_departures: number;
  capacity_profile_60: Array<{ tick: number; count: number }>;
  capacity_profile_15: Array<{ tick: number; count: number }>;
  capacity_profile_60_dep: Array<{ tick: number; count: number }>;
  capacity_profile_60_arr: Array<{ tick: number; count: number }>;
  capacity_profile_15_dep: Array<{ tick: number; count: number }>;
  capacity_profile_15_arr: Array<{ tick: number; count: number }>;
  peak_60: number;
  peak_15: number;
  peak_60_dep: number;
  peak_60_arr: number;
  peak_15_dep: number;
  peak_15_arr: number;
  violations_60: number;
  violations_15: number;
  execution_time_ms: number;
}

export interface HubOptimizeLiveSnapshot {
  unique_od_pairs: number;
  total_connections: number;
  peak_60: number;
  peak_15: number;
  violations_60: number;
  violations_15: number;
  capacity_profile_60: Array<{ tick: number; count: number }>;
  capacity_profile_15: Array<{ tick: number; count: number }>;
  capacity_profile_60_dep: Array<{ tick: number; count: number }>;
  capacity_profile_60_arr: Array<{ tick: number; count: number }>;
  capacity_profile_15_dep: Array<{ tick: number; count: number }>;
  capacity_profile_15_arr: Array<{ tick: number; count: number }>;
}

export interface SolverProgress {
  type: 'progress';
  objective: number;
  bound: number;
  gap_pct: number;
  time_s: number;
  solutions: number;
  live_snapshot?: HubOptimizeLiveSnapshot;
}

// Hub Bank Optimizer types
export interface HubOptimizeRequest {
  sample_week_id: string;
  version_number?: number;
  hub_airport: string;
  carrier: string;
  day_of_week: number;
  min_connection_time: number;
  max_connection_time: number;
  mct_mode?: MctMode;
  mct_version?: number | null;
  circuity_filter?: {
    relative_enabled: boolean;
    relative_limit: number;
    absolute_enabled: boolean;
    absolute_limit: number;
    use_and_logic: boolean;
  };
  cap_60_dep?: number;
  cap_60_arr?: number;
  cap_60_total?: number;
  cap_15_dep?: number;
  cap_15_arr?: number;
  cap_15_total?: number;
  max_retime_minutes: number;
  time_limit_seconds: number;
  cross_fleet?: boolean;
  max_ground_time?: number;
  od_weight?: number;
  touch_weight?: number;
  disruption_weight?: number;
  retime_weight?: number;
  hub_inventory_weight?: number;
  solver_backend?: 'cp_sat_baseline' | 'greedy_capacity_seed_cp_sat';
  od_weight_overrides?: Array<{ od: string; multiplier: number }>;
  locked_flights?: Array<{ carrier: string; flightnumber: string; direction: string }>;
}

export interface HubInventorySubfleetDiagnostic {
  subfleet: string;
  baseline_peak_required_stock: number;
  baseline_peak_time: string | null;
  optimized_peak_required_stock: number;
  optimized_peak_time: string | null;
  excess_stock: number;
}

export interface RetimedFlight {
  carrier: string;
  flightnumber: string;
  airport: string;
  direction: string;
  original_time: string;
  new_time: string;
  retime_minutes: number;
  od_gained: number;
  od_lost: number;
  markets_gained: [string, string][];
  markets_lost: [string, string][];
}

export interface VisualizationFlight {
  carrier: string;
  flightnumber: string;
  airport: string;
  direction: 'arrival' | 'departure';
  time: string;
  retime_minutes: number;
  is_hub_carrier: boolean;
}

export interface HubOptimizeResponse {
  solver_backend?: string;
  solver_metadata?: Record<string, unknown>;
  before: HubScoreResponse;
  after: HubScoreResponse;
  improvement: {
    od_pairs_delta: number;
    total_connections_delta: number;
  };
  retimed_flights: RetimedFlight[];
  visualization_flights: VisualizationFlight[];
  hub_inventory: {
    enabled: boolean;
    warning: boolean;
    penalty_weight: number;
    total_excess_aircraft: number;
    subfleets: HubInventorySubfleetDiagnostic[];
  };
  solver_stats: {
    status: string;
    wall_time_seconds: number;
    objective_value: number;
    objective_bound: number;
    optimality_gap_pct: number;
    num_movable_flights: number;
    num_candidate_pairs: number;
    num_modeled_markets: number;
    num_rotation_links: number;
    hub_inventory_penalty_weight: number;
    hub_inventory_total_excess: number;
    hub_inventory_warning: boolean;
  };
  execution_time_ms: number;
}

// Partnership Analysis types
export interface PartnershipHubParams {
  hub_airport: string;
  sample_week_id: string;
  min_connection_to: number;
  max_connection_to: number;
  min_connection_from: number;
  max_connection_from: number;
  mct_mode?: MctMode;
  mct_version?: number | null;
  version_number?: number | null;
  connection_type?: string;
  selected_regions?: string[];
  airline_filter?: string[];
  circuity_filter?: {
    relative_enabled: boolean;
    relative_limit: number;
    absolute_enabled: boolean;
    absolute_limit: number;
    use_and_logic: boolean;
  };
}

export interface PartnershipVariantResult {
  name: string;
  /** Airport this variant was analysed at; partner hubs can differ per variant (#972). */
  hub_airport?: string;
  market_connections: Array<{
    market: string;
    nonstop_weekly?: number | null;
    // Single-pair aggregation uses freq_to/freq_from
    freq_to?: number;
    freq_from?: number;
    // Grouped aggregation uses total_freq_to/total_freq_from
    total_freq_to?: number;
    total_freq_from?: number;
    pct_to?: number;
    pct_from?: number;
    min_pct_to?: number;
    min_pct_from?: number;
    min_elap_to?: string;
    min_elap_from?: string;
    conx_flt_to?: string;
    conx_flt_from?: string;
    label_to?: string | null;
    label_from?: string | null;
    country?: string;
    country_name?: string;
    region?: string;
    region_name?: string;
    circuity_relative?: number;
    circuity_absolute?: number;
    market_key?: string | null;
    passenger_pool?: number | null;
    average_fare?: number | null;
    revenue_pool?: number | null;
    market_value_available?: boolean;
    passenger_pool_source?: string | null;
    fare_source?: string | null;
    revenue_pool_source?: string | null;
    market_value_source_detail?: string | null;
  }>;
  detailed_connections: Record<string, { to: any[]; from: any[] }>;
  summary: {
    market_count: number;
    total_connections: number;
    passenger_pool?: number;
    revenue_pool?: number;
    custom_metric_score?: number | null;
  };
  delta?: {
    market_count: number;
    total_connections: number;
    passenger_pool?: number;
    revenue_pool?: number;
    custom_metric_score?: number | null;
  } | null;
  market_deltas?: Record<string, number>;
}

export interface PartnershipAnalysisResult {
  hub_1: {
    hub_airport: string;
    variants: Array<PartnershipVariantResult | null>;
  };
  hub_2: {
    hub_airport: string;
    variants: Array<PartnershipVariantResult | null>;
  };
  execution_time_ms: number;
  /** The requested custom metric is no longer available to the caller (e.g. unshared). */
  custom_metric_unavailable?: boolean;
}

// =============================================================================
// Airport Definition Types and API
// =============================================================================

export interface AirportDefinition {
  code: string;
  name: string;                 // Airport proper name (e.g. "Zayed International Airport")
  municipality: string;         // Raw city name from source data
  display_city: string;         // Clean city name for display (e.g., "Tokyo" instead of "Narita")
  region_code: string;          // Region code (e.g., "EUR", "AME")
  region_name: string;          // Region display name (e.g., "Europe", "Americas")
  country_code: string;
  country_name: string;         // Raw country name from source data
  display_country: string;      // Clean country name for display (e.g., "Venezuela" instead of full name)
  importance_index: number;
  timezone: string;
}

export interface AirportDefinitionVersion {
  id: string;
  version_number: number;
  is_latest: boolean;
  created_by: string | null;
  created_by_username: string | null;
  created_at: string;
  description: string;
  change_summary: {
    added?: number;
    modified?: number;
    deleted?: number;
    initial_load?: number;
  };
  airport_count: number;
}

export interface AirportListResponse {
  airports: AirportDefinition[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  version: {
    version_number: number;
    is_latest: boolean;
    created_at: string;
    airport_count: number;
  } | {
    version_number: null;
    is_latest: null;
    created_at: null;
    airport_count: null;
  };
}

export interface AirportVersionsResponse {
  versions: AirportDefinitionVersion[];
  count: number;
  has_data: boolean;
}

export interface AirportModifications {
  additions?: Array<Partial<AirportDefinition> & { code: string }>;
  updates?: Array<Partial<AirportDefinition> & { code: string }>;
  deletions?: Array<{ code: string }>;
  description?: string;
}

export const airportDefinitionApi = {
  supportsSnapshotVersion: true,
  // List all airports from latest version (or specific version)
  list: async (
    version?: number,
    page = 1,
    pageSize = 10000
  ): Promise<AirportListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/airport-definitions/list/?${params}`);
    return response.data;
  },

  // List all versions
  listVersions: async (): Promise<AirportVersionsResponse> => {
    const response = await api.get('/airport-definitions/versions/');
    return response.data;
  },

  // Initialize from raw data (creates v1)
  initialize: async (): Promise<{
    message: string;
    version: AirportDefinitionVersion;
  }> => {
    const response = await api.post('/airport-definitions/initialize/');
    return response.data;
  },

  // Save changes (creates new version)
  saveChanges: async (
    modifications: AirportModifications
  ): Promise<{
    message: string;
    version: AirportDefinitionVersion;
  }> => {
    const response = await api.post('/airport-definitions/save/', modifications);
    return response.data;
  },

  // Search airports
  search: async (
    query: string,
    version?: number,
    limit = 50
  ): Promise<{
    airports: AirportDefinition[];
    count: number;
    query: string;
  }> => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/airport-definitions/search/?${params}`);
    return response.data;
  },

  // Lookup single airport
  lookup: async (
    code: string,
    version?: number
  ): Promise<{ airport: AirportDefinition }> => {
    const params = version ? `?version=${version}` : '';
    const response = await api.get(`/airport-definitions/lookup/${code}/${params}`);
    return response.data;
  },
};

// =============================================================================
// Region Definition Types and API
// =============================================================================

export interface RegionDefinition {
  region_code: string;
  region_name: string;
  description: string;
  is_custom: boolean;
}

export interface RegionDefinitionVersion {
  id: string;
  version_number: number;
  is_latest: boolean;
  created_by: string | null;
  created_by_username: string | null;
  created_at: string;
  description: string;
  change_summary: {
    added?: number;
    modified?: number;
    deleted?: number;
    initial_load?: number;
  };
  region_count: number;
}

export interface RegionListResponse {
  regions: RegionDefinition[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  version: {
    version_number: number;
    is_latest: boolean;
    created_at: string;
    region_count: number;
  };
}

export interface RegionVersionsResponse {
  versions: RegionDefinitionVersion[];
  count: number;
  has_data: boolean;
}

export interface RegionModifications {
  additions?: Array<Partial<RegionDefinition> & { region_code: string }>;
  updates?: Array<Partial<RegionDefinition> & { region_code: string }>;
  deletions?: Array<{ region_code: string }>;
  description?: string;
}

export const regionDefinitionApi = {
  supportsSnapshotVersion: true,
  // List all regions from latest version (or specific version)
  list: async (
    version?: number,
    page = 1,
    pageSize = 1000
  ): Promise<RegionListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/region-definitions/list/?${params}`);
    return response.data;
  },

  // List all versions
  listVersions: async (): Promise<RegionVersionsResponse> => {
    const response = await api.get('/region-definitions/versions/');
    return response.data;
  },

  // Initialize from country data (creates v1)
  initialize: async (): Promise<{
    message: string;
    version: RegionDefinitionVersion;
  }> => {
    const response = await api.post('/region-definitions/initialize/');
    return response.data;
  },

  // Save changes (creates new version)
  saveChanges: async (
    modifications: RegionModifications
  ): Promise<{
    message: string;
    version: RegionDefinitionVersion;
  }> => {
    const response = await api.post('/region-definitions/save/', modifications);
    return response.data;
  },

  // Search regions
  search: async (
    query: string,
    version?: number,
    limit = 50
  ): Promise<{
    regions: RegionDefinition[];
    count: number;
    query: string;
  }> => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/region-definitions/search/?${params}`);
    return response.data;
  },

  // Lookup single region
  lookup: async (
    regionCode: string,
    version?: number
  ): Promise<{ region: RegionDefinition }> => {
    const params = version ? `?version=${version}` : '';
    const response = await api.get(`/region-definitions/lookup/${regionCode}/${params}`);
    return response.data;
  },
};

// =============================================================================
// Country Definition Types and API
// =============================================================================

export interface CountryDefinition {
  country_code: string;
  country_name: string;
  region_code: string;
}

export interface CountryDefinitionVersion {
  id: string;
  version_number: number;
  is_latest: boolean;
  created_by: string | null;
  created_by_username: string | null;
  created_at: string;
  description: string;
  change_summary: {
    added?: number;
    modified?: number;
    deleted?: number;
    initial_load?: number;
  };
  country_count: number;
}

export interface CountryListResponse {
  countries: CountryDefinition[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  version: {
    version_number: number;
    is_latest: boolean;
    created_at: string;
    country_count: number;
  };
}

export interface CountryVersionsResponse {
  versions: CountryDefinitionVersion[];
  count: number;
  has_data: boolean;
}

export interface CountryModifications {
  additions?: Array<Partial<CountryDefinition> & { country_code: string }>;
  updates?: Array<Partial<CountryDefinition> & { country_code: string }>;
  deletions?: Array<{ country_code: string }>;
  description?: string;
}

export const countryDefinitionApi = {
  supportsSnapshotVersion: true,
  // List all countries from latest version (or specific version)
  list: async (
    version?: number,
    page = 1,
    pageSize = 1000
  ): Promise<CountryListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/country-definitions/list/?${params}`);
    return response.data;
  },

  // List all versions
  listVersions: async (): Promise<CountryVersionsResponse> => {
    const response = await api.get('/country-definitions/versions/');
    return response.data;
  },

  // Initialize from airport data (creates v1)
  initialize: async (): Promise<{
    message: string;
    version: CountryDefinitionVersion;
  }> => {
    const response = await api.post('/country-definitions/initialize/');
    return response.data;
  },

  // Save changes (creates new version, triggers cache rebuild)
  saveChanges: async (
    modifications: CountryModifications
  ): Promise<{
    message: string;
    version: CountryDefinitionVersion;
    cache_rebuilt?: boolean;
    cache_airport_count?: number;
  }> => {
    const response = await api.post('/country-definitions/save/', modifications);
    return response.data;
  },

  // Search countries
  search: async (
    query: string,
    version?: number,
    limit = 50
  ): Promise<{
    countries: CountryDefinition[];
    count: number;
    query: string;
  }> => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
    });
    if (version) {
      params.set('version', version.toString());
    }
    const response = await api.get(`/country-definitions/search/?${params}`);
    return response.data;
  },

  // Lookup single country
  lookup: async (
    countryCode: string,
    version?: number
  ): Promise<{ country: CountryDefinition }> => {
    const params = version ? `?version=${version}` : '';
    const response = await api.get(`/country-definitions/lookup/${countryCode}/${params}`);
    return response.data;
  },

  // Get all countries in a region
  byRegion: async (
    regionCode: string,
    version?: number
  ): Promise<{
    countries: CountryDefinition[];
    count: number;
    region_code: string;
  }> => {
    const params = version ? `?version=${version}` : '';
    const response = await api.get(`/country-definitions/by-region/${regionCode}/${params}`);
    return response.data;
  },
};

// =============================================================================
// Minimum Connection Time Definitions API
// =============================================================================

export interface MinimumConnectionTimeVersion {
  id: string;
  version_number: number;
  is_latest: boolean;
  created_by: string | null;
  created_by_username: string | null;
  created_at: string;
  description: string;
  change_summary: Record<string, number>;
  source_filename: string;
  header_title: string;
  creator_reference: string;
  content_indicator: string;
  dataset_creation_date: string | null;
  dataset_creation_time: string;
  rule_count: number;
  partner_count: number;
}

export interface MinimumConnectionTimeRule {
  record_key: string;
  source_serial_number?: number | null;
  arrival_station?: string;
  time_hhmm?: string;
  time_minutes?: number | null;
  status: string;
  departure_station?: string;
  arrival_carrier?: string;
  arrival_codeshare_indicator?: string;
  arrival_codeshare_operating_carrier?: string;
  departure_carrier?: string;
  departure_codeshare_indicator?: string;
  departure_codeshare_operating_carrier?: string;
  arrival_aircraft_type?: string;
  arrival_aircraft_body?: string;
  departure_aircraft_type?: string;
  departure_aircraft_body?: string;
  arrival_terminal?: string;
  departure_terminal?: string;
  previous_country?: string;
  previous_station?: string;
  next_country?: string;
  next_station?: string;
  arrival_flight_number_start?: number | null;
  arrival_flight_number_end?: number | null;
  departure_flight_number_start?: number | null;
  departure_flight_number_end?: number | null;
  previous_state?: string;
  next_state?: string;
  previous_region?: string;
  next_region?: string;
  effective_from?: string | null;
  effective_to?: string | null;
  suppression_indicator?: string;
  suppression_region?: string;
  suppression_country?: string;
  suppression_state?: string;
  submitting_carrier?: string;
  filing_date?: string | null;
  action_indicator?: string;
}

export interface MinimumConnectionTimeListResponse {
  rules: MinimumConnectionTimeRule[];
  count: number;
  offset: number;
  page: number;
  page_size: number;
  total_pages: number;
  version: MinimumConnectionTimeVersion;
}

export interface MinimumConnectionTimeVersionsResponse {
  versions: MinimumConnectionTimeVersion[];
  count: number;
  has_data: boolean;
  /** IATA MCT region codes a rule may name. */
  region_codes?: string[];
}

export interface MinimumConnectionTimeModifications {
  additions?: MinimumConnectionTimeRule[];
  updates?: MinimumConnectionTimeRule[];
  deletions?: Array<{ record_key: string }>;
  description?: string;
}

export const minimumConnectionTimeApi = {
  list: async (params: {
    version?: number;
    page?: number;
    /** Row offset; takes precedence over page so a scrolling grid can ask for any range. */
    offset?: number;
    pageSize?: number;
    arrivalStation?: string;
    departureStation?: string;
    status?: string;
    carrier?: string;
    q?: string;
    suppressionOnly?: boolean;
    columnFilters?: Record<string, unknown>;
    sortModel?: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  } = {}): Promise<MinimumConnectionTimeListResponse> => {
    const searchParams = new URLSearchParams({
      page: String(params.page ?? 1),
      page_size: String(params.pageSize ?? 100),
    });
    if (params.version) searchParams.set('version', String(params.version));
    if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
    if (params.arrivalStation) searchParams.set('arrival_station', params.arrivalStation);
    if (params.departureStation) searchParams.set('departure_station', params.departureStation);
    if (params.status) searchParams.set('status', params.status);
    if (params.carrier) searchParams.set('carrier', params.carrier);
    if (params.q) searchParams.set('q', params.q);
    if (params.suppressionOnly) searchParams.set('suppression_only', 'true');
    if (params.columnFilters && Object.keys(params.columnFilters).length > 0) {
      searchParams.set('column_filters', JSON.stringify(params.columnFilters));
    }
    if (params.sortModel && params.sortModel.length > 0) {
      searchParams.set('sort_model', JSON.stringify(params.sortModel));
    }
    const response = await api.get(`/minimum-connection-times/?${searchParams.toString()}`);
    return response.data;
  },

  listVersions: async (): Promise<MinimumConnectionTimeVersionsResponse> => {
    const response = await api.get('/minimum-connection-times/versions/');
    return response.data;
  },

  initialize: async (): Promise<{
    message: string;
    version: MinimumConnectionTimeVersion;
  }> => {
    const response = await api.post('/minimum-connection-times/initialize/');
    return response.data;
  },

  save: async (data: MinimumConnectionTimeModifications): Promise<{
    message: string;
    version: MinimumConnectionTimeVersion;
  }> => {
    const response = await api.post('/minimum-connection-times/save/', data);
    return response.data;
  },
};

// =============================================================================
// Trunk Optimizer Types and API
// =============================================================================

export interface OptimizerAirline {
  code: string;
  home_hub: 1 | 2;
  daily_rotations: number;
  /** Per-carrier timing constraints (#825); each falls back to the
   * request-level value when omitted. Sending curfews (even []) replaces
   * the shared list for this carrier. */
  block_time_h1_to_h2?: number | null;
  block_time_h2_to_h1?: number | null;
  outstation_ground_time_min?: number | null;
  outstation_ground_time_max?: number | null;
  curfews?: OptimizerCurfew[] | null;
}

export interface OptimizerCurfew {
  airport: string;
  start_time: number;
  end_time: number;
}

export type OptimizeTrunkObjective = 'total' | 'total_hub1' | 'total_hub2' | 'balanced';

export interface OptimizeTrunkBaselineFlight {
  id?: string;
  flight_number: string;
  departure: string;
  arrival: string;
  departure_time: string;
  arrival_time: string;
  dow: string;
  tag?: { text: string; color: string };
  block_time?: number | null;
  time_mode?: 'dep' | 'arr';
}

export interface OptimizeTrunkPayload {
  hub_1: PartnershipHubParams;
  hub_2: PartnershipHubParams;
  airlines: OptimizerAirline[];
  baseline_trunk_flights?: OptimizeTrunkBaselineFlight[];
  source_trunk_flights?: OptimizeTrunkBaselineFlight[];
  target_flight_ids?: string[];
  retime_settings?: {
    home_hub: 1 | 2;
    outstation_ground_time_min: number;
    outstation_ground_time_max: number;
    curfews: OptimizerCurfew[];
  };
  block_time_h1_to_h2: number;
  block_time_h2_to_h1: number;
  outstation_ground_time_min: number;
  outstation_ground_time_max: number;
  curfews: OptimizerCurfew[];
  objective?: OptimizeTrunkObjective;
  objective_function?: OptimizationObjectiveFunction;
  custom_metric_id?: string;
  counting_mode?: CountingMode;
  label_set_id?: number | null;
  /** Nonstop-served markets (#866): when true (the default), a connection is
   * worth nothing on days a partner already operates that market non-stop out
   * of the origin. */
  discount_nonstop_served_markets?: boolean;
  /** Connection value profile (#818): soft window edges for the optimizer
   * objective; same contract as the route optimizer's. */
  value_profile?: import('./routeOptimizerApi').ValueProfileParams | null;
}

/** Per-hub strict + profile-weighted figures for the returned trunk schedule
 * (#818). Both are real-counter values; the weighted one is the active
 * objective when a profile is on. */
export interface TrunkValueProfileHubScores {
  strict_score: number;
  weighted_score: number;
}

export interface TrunkValueProfileSummary {
  active: boolean;
  objective_function: OptimizationObjectiveFunction;
  metric: string;
  hub_1: TrunkValueProfileHubScores;
  hub_2: TrunkValueProfileHubScores;
  /** True when the guard accepted a schedule whose strict metric dropped
   * while its weighted value rose — the trade the conditional note names. */
  strict_decreased_weighted_increased: boolean;
}

export interface OptimizationMetricBreakdown {
  connection_depth: number;
  markets_covered: number;
  passenger_pool: number;
  revenue_pool: number;
}

export interface OptimizeTrunkResult {
  flights: Array<{
    source_id?: string | null;
    change?: 'fixed' | 'retimed' | 'added';
    time_mode?: 'dep' | 'arr';
    flight_number: string;
    departure: string;
    arrival: string;
    departure_time: string;
    arrival_time: string;
    dow: string;
    block_time_minutes: number;
    tag_text: string;
    tag_color: string;
  }>;
  optimization_summary: {
    mode?: 'new_rotation' | 'retime_rotation' | 'retime_leg';
    source_kept?: boolean;
    fixed_flight_count?: number;
    source_flight_count?: number;
    requested_rotations?: number;
    selected_rotations?: number;
    rotation_shortfall?: number;
    before_score?: number;
    after_score?: number;
    weighted_before_score?: number;
    weighted_after_score?: number;
    comparison_baseline_score?: number | null;
    selection_proven?: boolean;
    total_weekly_connections: number;
    custom_metric?: import('./customMetricsApi').CustomMetricResult;
    objective_function?: OptimizationObjectiveFunction;
    objective_score?: number;
    metrics?: OptimizationMetricBreakdown;
    /** How the schedule was selected (#479): 'balanced_totals' scores the
     * geometric mean of the two hubs' totals across the whole selected
     * schedule, 'coverage_union' counts each covered unit once across the
     * schedule, 'additive' sums per-rotation scores. */
    selection_objective?: 'additive' | 'coverage_union' | 'balanced_totals';
    /** 'joint' when multiple carriers were selected in one model so they
     * split coverage instead of duplicating banks (#479). */
    coordination?: 'joint' | 'sequential' | 'independent';
    /** [hub1_total, hub2_total] behind the balanced score; null when the
     * solve was not balanced-on-totals. */
    joint_hub_scores?: [number, number] | null;
    /** Set when a balanced or union solve exhausted its budget and returned
     *  the greedy pick instead of a proven optimum (#479, #681). */
    selection_fallback?: 'greedy' | null;
    /** Present only when an oversized union pool was heuristically reduced. */
    selection_compaction?: { raw: number; kept: number };
    /** Outcome of the bounded raw-pool verify-and-improve pass (#906). */
    selection_verification?: {
      status: 'proven' | 'improved' | 'unproven';
      attempted: number;
      proven: number;
      improved: number;
      unproven: number;
    };
    /** Present only when a selector solve stopped at its time limit (#959). */
    selection_time_limit?: { solves: number };
    /** What the nonstop-served markets toggle actually did (#866/#875/#881).
     * `applied` false with `requested` true means the discount was asked for
     * but found nothing to discount, or could not be resolved — `status` says
     * which, so an unchanged answer is never ambiguous. */
    nonstop_discount?: {
      requested: boolean;
      applied: boolean;
      status:
        | 'applied'
        | 'disabled'
        | 'no_partner_carriers'
        | 'no_sample_week'
        | 'missing_carrier_column'
        | 'lookup_failed'
        | 'no_partner_nonstops'
        | 'no_served_markets';
      /** Both directions since #881; the origin day is the spoke leg's own
       * day inbound and the trunk leg's day outbound. */
      scope: 'both';
      partner_carriers: string[];
      partner_nonstop_markets: number;
      /** Distinct spoke markets the discount was configured to drop, both
       * directions of one market counted once (#886). An UPPER BOUND on the
       * markets the returned schedule actually lost connections in — the masks
       * are resolved before they meet the loaded spokes or the selected
       * timings — so anything displaying it must hedge. */
      discounted_markets: number;
      /** Exact real-counter reconciliation for the returned schedule (#886).
       * Present when `status` is `applied`; both totals use the request's
       * counting mode, and their difference is the amount hidden from the
       * discounted optimizer result but retained by Partnership/Analyse. */
      discounted_weekly_connections?: number;
      undiscounted_weekly_connections?: number;
      excluded_weekly_connections?: number;
      error?: string | null;
    } | null;
    /** 'real_counter_after_refine' when an accepted refine replaced the
     * selection-time headline numbers with real-counter values (#851). */
    metrics_source?: string | null;
    /** 'not_attributed_after_refine' when refined times invalidated the
     * per-rotation/airline score attributions (their fields are null). */
    rotation_metrics_source?: string | null;
    /** Local-search refine of the CP-SAT schedule against the real counter
     * (#851) — parity with the route optimizer's refinement block. */
    refinement?: {
      applied: boolean;
      skipped?: string;
      search?: 'fast_hub_counter' | 'real_counter' | 'real_counter_weighted';
      passes?: number;
      evaluations?: number;
      moves?: Array<{
        airline: string;
        flight_index: number;
        delta_minutes: number;
        search_score: number;
      }>;
      airline_skips?: Array<{ airline: string; reason: string }>;
      budget_exhausted?: boolean;
      base_score?: number;
      final_score?: number;
      improvement?: number;
      search_base_score?: number;
      search_final_score?: number;
      /** 'value_profile' when the accept decision compared the weighted
       * scores below instead of the strict ones (#818). */
      objective?: 'value_profile';
      weighted_base_score?: number;
      weighted_final_score?: number;
      counter_parity_mismatch?: boolean;
      rejected?: string;
      elapsed_ms?: number;
    } | null;
    baseline_guard?: {
      active: boolean;
      baseline_feasible?: boolean;
      infeasible_reason?: string | null;
      accepted: boolean;
      returned: 'generated' | 'baseline';
      objective: OptimizeTrunkObjective;
      objective_function: OptimizationObjectiveFunction;
      metric: string;
      baseline_score: number | null;
      generated_score: number | null;
      score_delta: number | null;
      /** 'value_profile' when the accept decision compared the weighted
       * scores below instead of the strict ones (#818). */
      comparison?: 'value_profile';
      weighted_baseline_score?: number;
      weighted_generated_score?: number;
      weighted_score_delta?: number;
      /** 'applied' when both scores above are real-counter values with the
       * nonstop-served markets discount applied (#866/#875) — discounted
       * against discounted, the quantity the optimizer maximised. */
      nonstop_discount?: 'applied';
      warnings?: string[];
      message: string;
    } | null;
    value_profile?: TrunkValueProfileSummary | null;
    airlines: Array<{
      code: string;
      /** Null after an accepted refine (see rotation_metrics_source). */
      weekly_connections: number | null;
      objective_score?: number | null;
      metrics?: OptimizationMetricBreakdown | null;
      rotations: Array<{
        rotation_number: number;
        weekly_connections: number | null;
        connection_depth?: number | null;
        objective_score?: number | null;
        metrics?: OptimizationMetricBreakdown | null;
        dep_home: string;
        arr_outstation: string;
        ground_time: number;
        dep_outstation: string;
        arr_home: string;
      }>;
    }>;
    execution_time_ms: number;
  };
}

// =============================================================================
// Block Time Prediction API
// =============================================================================

export const predictBlockTime = async (
  origin: string,
  destination: string
): Promise<{ predicted_minutes: number; method: string; distance_km: number }> => {
  const response = await api.get('/predict-block-time/', {
    params: { origin, destination },
  });
  return response.data;
};

// Tenant / Feature Flags

export interface TenantFeatures {
  od_itinerary_compare?: boolean;
  custom_market_metrics?: boolean;
  schedule_explorer: boolean;
  schedule_visualize: boolean;
  connection_analysis: boolean;
  schedule_comparison: boolean;
  partnership_analysis: boolean;
  bank_optimizer: boolean;
  mct_management: boolean;
  mct_dataset_mode: boolean;
  workspaces: boolean;
  org_sharing: boolean;
  route_optimizer: boolean;
  hub_reports: boolean;
  schedule_merge: boolean;
  feedback: boolean;
  connection_value_profile: boolean;
  agent_api: boolean;
  agent_investigations?: boolean;
}

export type AuthenticationMode = 'email' | 'trial_username';

export interface TenantAuthentication {
  mode: AuthenticationMode;
  password_recovery: boolean;
  user_management: boolean;
}

export interface TenantConfig {
  tenant_id: string;
  branding: { name: string; logo_url: string | null; theme_color: string | null };
  ui?: {
    environment_badge?: string | null;
  };
  authentication?: TenantAuthentication;
  features: TenantFeatures;
  defaults: {
    hub_airport: string;
    carrier: string;
    min_connection_to: number;
    max_connection_to: number;
    min_connection_from: number;
    max_connection_from: number;
    connection_type: string;
  };
  data: {
    visible_sample_week_ids: string[] | null;
    global_schedule_status_url: string | null;
  };
}

export interface GlobalScheduleStatus {
  version?: string;
  status_source?: 'remote' | 'local';
  coverage?: {
    start_date?: string | null;
    end_date?: string | null;
  };
  source?: {
    filename?: string;
    timestamp_utc?: string;
  };
  processed_at_utc?: string;
}

export const tenantApi = {
  getConfig: async (): Promise<TenantConfig> => {
    const response = await api.get('/tenant-config/');
    return response.data;
  },
};

export const globalScheduleApi = {
  getStatus: async (): Promise<GlobalScheduleStatus> => {
    const response = await api.get('/global-schedule/status/');
    return response.data;
  },
};

export default api;
